import DatePicker from '../core/DatePicker';
import { useInputPropsWithDefaultWidth } from '../hooks/useInputPropsWithDefaultWidth';

import type { SingleDatePickerChangeHandler, SingleDatePickerProps } from '../types';

/**
 * Inline 형태의 DateTimePicker 컴포넌트의 Props입니다.
 *
 * @example
 * ```tsx
 * <InlineDateTimePicker
 *   selected={dateTime}
 *   onChange={(date) => setDateTime(date)}
 *   showSeconds={true}
 * />
 * ```
 */
export interface InlineDateTimePickerProps
  extends Omit<SingleDatePickerProps, 'showTime' | 'inline'> {
  /**
   * 날짜 변경 시 호출되는 함수입니다.
   */
  onChange?: SingleDatePickerChangeHandler;

  /**
   * 선택된 날짜를 지정합니다.
   */
  selected?: Date | null;

  /**
   * 초 선택 필드 표시 여부를 설정합니다.
   * @default true
   */
  showSeconds?: boolean;
}

/**
 * DatePicker를 wrapping하여 inline 형태의 시간 선택 전용으로 사용하는 컴포넌트입니다.
 * 내부적으로 showTime과 inline을 true로 고정하여 DatePicker에 전달합니다.
 * Popper 없이 input 하단에 캘린더가 항상 표시됩니다.
 * inputProps에 width가 설정되지 않은 경우 캘린더 너비(352px)에 맞춰 자동 설정됩니다.
 */
const InlineDateTimePicker = (props: InlineDateTimePickerProps) => {
  const { inputProps, ...restProps } = props;

  // inputProps의 style에 width가 없으면 캘린더 너비(352px)로 설정
  const enhancedInputProps = useInputPropsWithDefaultWidth(inputProps, '352px');

  return <DatePicker {...restProps} inputProps={enhancedInputProps} showTime inline />;
};

InlineDateTimePicker.displayName = 'InlineDateTimePicker';

export default InlineDateTimePicker;
